const hexPattern = /^#([A-F0-9]{3}){1,2}$/i;

export function hexToRgbParts(hex: string): [number, number, number] | undefined {
  if (!hexPattern.test(hex)) {
    return;
  }

  const h = hex.substring(1);
  const l = h.length;
  const r = parseInt(h.substring(0, l / 3), 16);
  const g = parseInt(h.substring(l / 3, (2 * l) / 3), 16);
  const b = parseInt(h.substring((2 * l) / 3, (3 * l) / 3), 16);

  return [r, g, b];
}

function isValidRgbValue(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 255;
}

function isValidAlphaValue(value: number): boolean {
  return !isNaN(value) && value >= 0 && value <= 1;
}

export function rgbStringToRgbParts(
  rgbString: string,
): [number, number, number] | [number, number, number, number] | undefined {
  // Remove whitespace and convert to lowercase
  const cleaned = rgbString.replace(/\s/g, '').toLowerCase();

  // Match rgb(r, g, b) format
  const rgbMatch = cleaned.match(/^rgb\((\d+),(\d+),(\d+)\)$/);

  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Validate RGB values
    if (!isValidRgbValue(r) || !isValidRgbValue(g) || !isValidRgbValue(b)) {
      return undefined;
    }

    return [r, g, b];
  }

  // Match rgba(r, g, b, a) format
  const rgbaMatch = cleaned.match(/^rgba\((\d+),(\d+),(\d+),([\d.]+)\)$/);

  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    const a = parseFloat(rgbaMatch[4]);

    // Validate RGB and alpha values
    if (
      !isValidRgbValue(r) ||
      !isValidRgbValue(g) ||
      !isValidRgbValue(b) ||
      !isValidAlphaValue(a)
    ) {
      return undefined;
    }

    return [r, g, b, a];
  }
}

export function hexToRgba(hex: string, alpha?: number): string | undefined {
  const parts = hexToRgbParts(hex);

  if (!parts) {
    return;
  }

  const [r, g, b] = parts;
  const formatPart = alpha !== undefined ? 'rgba' : 'rgb';
  const alphaPart = alpha !== undefined ? `, ${alpha}` : '';

  return `${formatPart}(${r}, ${g}, ${b}${alphaPart})`;
}

export const blendRgbaColors = (
  fg: [number, number, number, number],
  bg: [number, number, number, number],
): [number, number, number, number] => {
  const [fgR, fgG, fgB, fgAlpha] = fg;
  const [bgR, bgG, bgB, bgAlpha] = bg;

  const alpha = fgAlpha + bgAlpha * (1 - fgAlpha);

  const blend = (fgComp: number, bgComp: number): number =>
    (fgComp * fgAlpha + bgComp * bgAlpha * (1 - fgAlpha)) / alpha;

  const r = Math.round(blend(fgR, bgR));
  const g = Math.round(blend(fgG, bgG));
  const b = Math.round(blend(fgB, bgB));

  return [r, g, b, alpha];
};
